import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ChevronRight, Check } from 'lucide-react';
import FormBuilderWithLayout, { FormField } from '@/components/company/FormBuilderWithLayout';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import { VisionSkeleton } from '@/components/company/skeletons';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import { useGetCompanyData } from './company-data/useCompanyData';
import SwitchButton from '@/components/company/SwitchButton';
import StepTipsPanel from '@/components/company/StepTipsPanel';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';

interface VisionFormValues {
  category: string;
  dna: string;
  area: string;
  target: string;
  vision: string;
  is_auto_generate: boolean;
  is_published_vision: boolean;
}

export default function Vision() {
  const formControl = useForm<VisionFormValues>({
    defaultValues: {
      category: '',
      dna: '',
      area: '',
      target: '',
      vision: '',
      is_auto_generate: true,
      is_published_vision: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const { currentTenant } = useTenantState();
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const isReadOnly = useIsReadOnly();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();
  const isPublished = watch('is_published_vision');
  const [showPanel, setShowPanel] = useState(true);
  const { data: companyData } = useGetCompanyData(currentTenant?.id);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();

  const visionStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'vision');
  const videoLink = visionStep?.video_link;
  const tips = visionStep?.tips;

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const autosave = useAutosave({
    form: formControl,
    formKey: 'vision',
    enabled: isDataLoaded && !isReadOnly,
    // is_auto_generate is intentionally INCLUDED in the draft. The effect below
    // regenerates `vision` from the source fields only while that flag is true,
    // so a draft of a manually written vision must carry the flag that keeps it
    // from being overwritten on restore.
    //
    // is_published_vision is excluded: it is server-owned and a stale draft
    // value must not silently republish or unpublish.
    exclude: ['is_published_vision'],
  });

  useEffect(() => {
    if (isLoading) return;

    if (!companyProfile) {
      setIsDataLoaded(true);
      return;
    }

    const visionData = Array.isArray(companyProfile.vision) ? companyProfile.vision[0] : null;

    const positioningData = Array.isArray(companyProfile.positioning) ? companyProfile.positioning[0] : null;

    const businessCategory = companyData?.business_category ?? '';

    let category = '';

    if (visionData?.category) {
      category = visionData.category;
    } else if (positioningData?.category && positioningData.category !== businessCategory) {
      category = positioningData.category;
    } else {
      category = businessCategory;
    }

    const dna = visionData?.dna || positioningData?.dna || '';

    let coreValue = '';

    if (positioningData?.core_value) {
      coreValue = positioningData.core_value.split('[ADD_VALUE]')[0].trim();
    }

    const area = visionData?.area ?? '';
    const target = visionData?.target ?? '';
    const visionValue = visionData?.vision ?? '';

    const areaValue = area || '[wilayah]';
    const targetYear = target || '[target tahun]';
    const categoryValue = category || '[category]';
    const dnaValue = dna || '[dna]';
    const coreValueText = coreValue ? `${coreValue}` : '';

    const autoGenStr = `Menjadi ${categoryValue} ${dnaValue} ${coreValueText} di ${areaValue} pada tahun ${targetYear}`;

    let isAutoGenerate = true;
    if (visionValue && visionValue !== autoGenStr) {
      isAutoGenerate = false;
    }

    const generatedVision = visionValue || autoGenStr;

    reset({
      category,
      dna,
      area,
      target,
      vision: generatedVision,
      is_auto_generate: isAutoGenerate,
      is_published_vision: !!companyProfile.is_published_vision,
    });

    setIsDataLoaded(true);
    // Depend on the primitive business_category rather than the companyData object, whose
    // identity can change every render and would re-run reset() in an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, companyProfile, companyData?.business_category, reset]);

  const handleSave = async (data: VisionFormValues) => {
    if (!currentTenant?.id) return;

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      toast.info('Anda sedang offline. Perubahan disimpan sebagai draft di perangkat ini.');
      return;
    }

    const payload = [
      {
        category: data.category,
        dna: data.dna,
        area: data.area,
        target: data.target,
        vision: data.vision,
      },
    ];

    await updateFieldMutation.mutateAsync({
      tenantId: currentTenant.id,
      field: 'vision',
      value: payload,
      isPublished: data.is_published_vision,
    });

    // Server now holds these values; the draft would only be noise on return.
    autosave.clearDraft();
    toast.success('Visi berhasil disimpan');
    navigate(`/${tenant_id}/mission`);
  };

  const hasPendingDraft = !!autosave.pendingDraft;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (['category', 'dna', 'area', 'target'].includes(name as string)) {
        if (!isDataLoaded) return;
        // While a draft is being offered, the form still shows server values.
        // Regenerating now would overwrite `vision` before the user has chosen,
        // and the restore would then be compared against mutated state.
        if (hasPendingDraft) return;
        const current = formControl.getValues();
        if (!current.is_auto_generate) return;

        const category = current.category || '[category]';
        const dna = current.dna || '[dna]';
        const rawCoreValue =
          (Array.isArray(companyProfile?.positioning) ? companyProfile.positioning[0]?.core_value : '') || '';

        const coreValue = rawCoreValue ? rawCoreValue.split('[ADD_VALUE]')[0].trim() : '[core value]';

        const area = current.area || '[wilayah]';
        const target = current.target || '[target tahun]';

        const generated = `Menjadi ${category} ${dna} ${coreValue} di ${area} pada tahun ${target}`;

        setValue('vision', generated, { shouldDirty: false });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, companyProfile, formControl, setValue, isDataLoaded, hasPendingDraft]);

  const fields: FormField[] = [
    {
      name: 'category',
      label: 'Category',
      type: 'markdown',
      placeholder: 'Contoh: Warung Makan',
    },
    {
      name: 'dna',
      label: 'DNA',
      type: 'markdown',
      placeholder: 'Contoh: Bakso',
    },
    {
      name: 'area',
      label: 'Wilayah',
      type: 'markdown',
      placeholder: 'Contoh: Indonesia',
      required: true,
      colSpan: 2,
    },
    {
      name: 'target',
      label: 'Target Tahun',
      type: 'markdown',
      placeholder: 'Contoh: 2030',
      required: true,
      colSpan: 2,
    },
    {
      name: 'vision',
      label: 'Visi Perusahaan',
      labelRight: (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-normal">Gunakan Teks Auto-Generated</span>
          <SwitchButton
            checked={watch('is_auto_generate')}
            onChange={val => setValue('is_auto_generate', val, { shouldDirty: true })}
            disabled={isReadOnly}
          />
        </div>
      ),
      type: 'markdown',
      required: true,
      disabled: watch('is_auto_generate'),
      placeholder: !watch('vision')
        ? (() => {
            const rawCoreValue =
              (Array.isArray(companyProfile?.positioning) ? companyProfile.positioning[0]?.core_value : '') || '';

            const coreValue = rawCoreValue ? rawCoreValue.split('[ADD_VALUE]')[0].trim() : '[core value]';

            return `Menjadi ${watch('category') || '[category]'} ${watch('dna') || '[dna]'} ${coreValue} di ${
              watch('area') || '[wilayah]'
            } pada tahun ${watch('target') || '[target tahun]'}`;
          })()
        : '',
      colSpan: 2,
    },
  ];

  if (isLoading) {
    return <VisionSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  return (
    <section className="w-full">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Vision</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Anda akan merancang kondisi ideal bisnis anda selama 5 tahun ke depan.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}

          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
            {autosave.pendingDraft && (
              <DraftBanner
                savedAt={autosave.pendingDraft.savedAt}
                pendingSync={autosave.pendingDraft.pendingSync}
                onRestore={autosave.restoreDraft}
                onDiscard={autosave.discardDraft}
              />
            )}

            <FormProvider {...formControl}>
              <form ref={formRef} onSubmit={handleSubmit(handleSave)}>
                <FormBuilderWithLayout
                  fields={fields}
                  initialPublished={!!isPublished}
                  onPublishChange={val => setValue('is_published_vision', val)}
                  disabled={isReadOnly}
                  dark
                />
              </form>
            </FormProvider>
          </div>
        </div>

        {showPanel && (
          <div className="w-full xl:w-[420px] 2xl:w-[600px] shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
